import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Toaster, toast } from "react-hot-toast";
import { motion, AnimatePresence } from "framer-motion";
import Navbar from "../components/Navbar";

const API_BASE = process.env.REACT_APP_URL;

// Adds JWT token to axios requests
const authConfig = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` }
});

export default function AdminManage() {
  const navigate = useNavigate();
  const [admins, setAdmins] = useState([]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [darkMode, setDarkMode] = useState(false);
  const SUPER_ADMIN = "admin@navriti.com";
  const storedEmail = localStorage.getItem("adminEmail");
  const isSuperAdmin = storedEmail === SUPER_ADMIN;

  useEffect(() => {
    const syncTheme = () => {
      const savedMode = localStorage.getItem("darkMode");
      setDarkMode(savedMode ? JSON.parse(savedMode) : false);
    };
    syncTheme();
    window.addEventListener("themeChanged", syncTheme);
    return () => window.removeEventListener("themeChanged", syncTheme);
  }, []);

  const fetchAdmins = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_BASE}/admins/all`, authConfig());
      setAdmins(res.data || []);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load admins");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAdmins();
  }, []);

  const addAdmin = async () => {
    if (!email.trim()) {
      toast("Enter an email first");
      return;
    }

    if (!isSuperAdmin) {
      toast.error("Only super admin can add admins");
      return;
    }

    try {
      const promise = axios.post(`${API_BASE}/admins/add`, { email }, authConfig());
      await toast.promise(promise, {
        loading: "Adding admin...",
        success: "Admin added successfully",
        error: (err) =>
          err.response?.data?.error || "Failed to add admin",
      });
      setEmail("");
      fetchAdmins();
    } catch (err) { }
  };

  const openDeleteModal = (admin) => {
    if (!isSuperAdmin) {
      toast.error("Only super admin can remove admins");
      return;
    }

    if (admin.email === SUPER_ADMIN) {
      toast("Super admin cannot be removed.", { icon: "🔒" });
      return;
    }

    setSelectedAdmin(admin);
    setModalOpen(true);
  };

  const removeAdmin = async () => {
    if (!selectedAdmin) return;

    if (!isSuperAdmin) {
      toast.error("Only super admin can remove admins");
      return;
    }

    try {
      const promise = axios.delete(
        `${API_BASE}/admins/${encodeURIComponent(selectedAdmin.email)}`,
        authConfig()
      );

      await toast.promise(promise, {
        loading: "Removing admin...",
        success: "Admin removed successfully",
        error: "Failed to remove admin",
      });

      setModalOpen(false);
      setSelectedAdmin(null);
      fetchAdmins();
    } catch (err) { }
  };

  const cardBg = "var(--card-bg)";
  const textMain = "var(--text-main)";
  const textSub = "var(--text-sub)";
  const tableHeaderBg = "#636CCB";

  return (
    <div className={darkMode ? "dark-mode" : ""} style={styles.page(darkMode)}>
      <Navbar />

      <Toaster position="top-right" />

      <div style={styles.contentContainer}>
        <div
          style={{
            padding: "70px 0 0 0",
            maxWidth: "1100px",
            margin: "0 auto",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginBottom: "25px",
              gap: "16px",
              padding: "0 40px",
            }}
          >
            <button
              onClick={() => navigate(-1)}
              style={{
                backgroundColor: "#636CCB",
                color: "white",
                border: "none",
                borderRadius: "999px",
                padding: "8px 18px",
                cursor: "pointer",
                fontSize: "0.95em",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
              }}
            >
              <span>⮜</span>
              <span>Back</span>
            </button>

            <div style={{ textAlign: "center", flex: 1 }}>
              <h2
                style={{
                  color: textMain,
                  margin: 0,
                  fontSize: "1.8rem",
                  fontWeight: 700,
                }}
              >
                Manage Admins
              </h2>
              <p
                style={{
                  color: textSub,
                  marginTop: "4px",
                  fontSize: "0.95rem",
                }}
              >
              </p>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.25 }}
            style={{
              background: cardBg,
              borderRadius: "18px",
              padding: "22px 24px 26px",
              boxShadow: "0 18px 45px rgba(15,23,42,0.25)",
              maxWidth: "1100px",
              margin: "0 40px 40px 40px",
              border: "1px solid var(--border-light)",
              transition: "background 0.3s, color 0.3s",
            }}
          >
            {isSuperAdmin && (
              <div
                style={{
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "12px",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "18px",
                }}
              >
                <div>
                  <h3
                    style={{
                      margin: 0,
                      color: textMain,
                      fontSize: "1.1rem",
                      fontWeight: 600,
                    }}
                  >
                    Add New Admin
                  </h3>
                  <p
                    style={{
                      margin: "4px 0 0",
                      color: textSub,
                      fontSize: "0.85rem",
                    }}
                  >
                    Enter an email to grant admin access.
                  </p>
                </div>

                <div
                  style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "10px",
                    alignItems: "center",
                  }}
                >
                  <input
                    type="email"
                    placeholder="admin@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    style={{
                      padding: "10px 12px",
                      minWidth: "240px",
                      borderRadius: "10px",
                      border: "1px solid var(--border-light)",
                      outline: "none",
                      fontSize: "0.9rem",
                      backgroundColor: "var(--card-bg)",
                      color: textMain,
                    }}
                  />
                  <button
                    onClick={addAdmin}
                    style={{
                      background:
                        "linear-gradient(135deg, #636CCB, #4f46e5)",
                      color: "white",
                      border: "none",
                      borderRadius: "999px",
                      padding: "10px 18px",
                      cursor: "pointer",
                      fontWeight: 500,
                      fontSize: "0.9rem",
                      boxShadow: "0 4px 12px rgba(79,70,229,0.5)",
                    }}
                  >
                    + Add Admin
                  </button>
                </div>
              </div>
            )}

            <div
              style={{
                height: "1px",
                backgroundColor: "var(--border-light)",
                marginBottom: "14px",
              }}
            />

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "baseline",
                marginBottom: "10px",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  color: textMain,
                  fontSize: "1.05rem",
                  fontWeight: 600,
                }}
              >
                Existing Admins
              </h3>
              <span
                style={{
                  fontSize: "0.8rem",
                  color: textSub,
                }}
              >
                Total: {admins.length}
              </span>
            </div>

            {loading ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "30px 10px 10px",
                  color: textSub,
                  fontSize: "0.95rem",
                }}
              >
                Loading admins...
              </div>
            ) : admins.length === 0 ? (
              <div
                style={{
                  textAlign: "center",
                  padding: "30px 10px 10px",
                  color: textSub,
                  fontSize: "0.95rem",
                }}
              >
                No admins added yet. Use the field above to add one.
              </div>
            ) : (
              <div style={{ overflowX: "auto", marginTop: "4px" }}>
                <table
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    fontSize: "0.9rem",
                  }}
                >
                  <thead>
                    <tr
                      style={{
                        background: tableHeaderBg,
                        color: "white",
                      }}
                    >
                      <th style={{ padding: "10px 12px", textAlign: "left" }}>
                        Email
                      </th>
                      <th style={{ padding: "10px 12px", textAlign: "left" }}>
                        Role
                      </th>
                      <th style={{ padding: "10px 12px", textAlign: "left" }}>
                        Added On
                      </th>
                      <th style={{ padding: "10px 12px", textAlign: "center" }}>
                        Action
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    <AnimatePresence>
                      {admins.map((a, index) => (
                        <motion.tr
                          key={a.email}
                          initial={{ opacity: 0, y: 8 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -8 }}
                          transition={{ duration: 0.18, delay: index * 0.03 }}
                          style={{
                            backgroundColor:
                              index % 2 === 0
                                ? "var(--zebra-stripe)"
                                : "transparent",
                          }}
                        >
                          <td
                            style={{
                              padding: "10px 12px",
                              color: textMain,
                              fontWeight:
                                a.email === SUPER_ADMIN ? 600 : 400,
                            }}
                          >
                            {a.email}
                          </td>

                          <td
                            style={{
                              padding: "10px 12px",
                              color:
                                a.email === SUPER_ADMIN
                                  ? "#22c55e"
                                  : "#6366f1",
                              fontWeight: 600,
                            }}
                          >
                            {a.email === SUPER_ADMIN ? "Super Admin" : "Admin"}
                          </td>

                          <td
                            style={{
                              padding: "10px 12px",
                              color: textSub,
                            }}
                          >
                            {a.createdAt
                              ? new Date(a.createdAt).toLocaleString()
                              : "—"}
                          </td>

                          <td
                            style={{
                              padding: "10px 12px",
                              textAlign: "center",
                            }}
                          >
                            {isSuperAdmin && a.email !== SUPER_ADMIN ? (
                              <button
                                onClick={() => openDeleteModal(a)}
                                style={{
                                  backgroundColor: "#ef4444",
                                  color: "white",
                                  border: "none",
                                  padding: "6px 14px",
                                  borderRadius: "999px",
                                  cursor: "pointer",
                                  fontSize: "0.8rem",
                                  fontWeight: 500,
                                  boxShadow:
                                    "0 3px 8px rgba(239,68,68,0.6)",
                                }}
                              >
                                Delete
                              </button>
                            ) : (
                              <span
                                style={{
                                  fontSize: "0.8rem",
                                  color: "#22c55e",
                                  fontWeight: 600,
                                }}
                              >
                                {a.email === SUPER_ADMIN
                                  ? "Protected"
                                  : "View Only"}
                              </span>
                            )}
                          </td>
                        </motion.tr>
                      ))}
                    </AnimatePresence>
                  </tbody>
                </table>
              </div>
            )}
          </motion.div>
        </div>
      </div>
      <AnimatePresence>
        {modalOpen && selectedAdmin && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(15,23,42,0.6)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 2000,
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0, y: 10 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 10 }}
              transition={{ duration: 0.2 }}
              style={{
                background:
                  "linear-gradient(135deg, rgba(15,23,42,0.96), rgba(30,64,175,0.96))",
                borderRadius: "18px",
                padding: "22px 24px 18px",
                width: "90%",
                maxWidth: "420px",
                color: "white",
                boxShadow: "0 24px 60px rgba(15,23,42,0.8)",
                border: "1px solid rgba(148,163,184,0.5)",
              }}
            >
              <h3
                style={{
                  margin: 0,
                  marginBottom: "8px",
                  fontSize: "1.2rem",
                }}
              >
                Remove admin access?
              </h3>

              <p
                style={{
                  margin: "4px 0 10px",
                  fontSize: "0.9rem",
                  color: "#e5e7eb",
                }}
              >
                You are about to remove admin access for:
              </p>

              <p
                style={{
                  margin: 0,
                  fontSize: "0.9rem",
                  fontWeight: 600,
                  color: "#bfdbfe",
                  wordBreak: "break-all",
                }}
              >
                {selectedAdmin.email}
              </p>

              <p
                style={{
                  marginTop: "12px",
                  fontSize: "0.8rem",
                  color: "#facc15",
                }}
              >
                This action can be re-done later by adding the same email again.
              </p>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "10px",
                  marginTop: "18px",
                }}
              >
                <button
                  onClick={() => {
                    setModalOpen(false);
                    setSelectedAdmin(null);
                  }}
                  style={{
                    borderRadius: "999px",
                    border: "1px solid rgba(148,163,184,0.6)",
                    backgroundColor: "transparent",
                    color: "#e5e7eb",
                    padding: "6px 16px",
                    cursor: "pointer",
                    fontSize: "0.85rem",
                  }}
                >
                  Cancel
                </button>

                {isSuperAdmin && (
                  <button
                    onClick={removeAdmin}
                    style={{
                      borderRadius: "999px",
                      border: "none",
                      background:
                        "linear-gradient(135deg,#ef4444,#b91c1c)",
                      color: "white",
                      padding: "6px 18px",
                      cursor: "pointer",
                      fontSize: "0.85rem",
                      fontWeight: 500,
                      boxShadow: "0 4px 12px rgba(239,68,68,0.8)",
                    }}
                  >
                    Yes, Remove
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <footer style={styles.footer}>
        Navriti Technologies © All rights reserved
      </footer>
    </div>
  );
}

const styles = {
  page: (darkMode) => ({
    minHeight: "100vh",
    background: darkMode ? "transparent" : "linear-gradient(135deg, #f4f2f8, #eae6f3)",
    fontFamily: "Poppins, sans-serif",
    paddingBottom: "80px",
    transition: "background 0.3s ease",
    overflowX: "hidden",
    width: "100vw",
  }),

  contentContainer: {
  },

  footer: {
    width: "100%",
    backgroundColor: "#636CCB",
    color: "white",
    textAlign: "center",
    height: "40px",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontWeight: "600",
    fontSize: "1em",
    position: "fixed",
    bottom: 0,
    left: 0,
    zIndex: 2000,
  },
};